<template>
  <iSelect
    :value="value"
    :placeholder="placeholder"
    :disabled="isDisabled"
    :readonly="readonly"

    :multiple="multiple"
    :filterable="isFilterable"
    :clearable="isClearable"

    :size="size"
    :transfer="transfer"

    :class="$style.select"

    v-bind="remote ? {
      remote: true,
      loading: remoteData.loading,
      remoteMethod: remoteMethod,
    } : {}"

    @on-change="onChange"
    @focus.native="$emit('focus', $event)"
    @blur.native="$emit('blur', $event)"
  >
    <slot v-if="$slots.default"/>
    <template v-else>
      <i-option
        v-for="item in optionsList"
        :key="item.value"
        :value="item.value"
        :label="item.label"
      />
      <template v-if="remote">
        <i-option
          v-for="item in optionsCached"
          :key="`cached-${item.value}`"
          :value="item.value"
          :label="item.label"
        />
      </template>
    </template>


  </iSelect>
</template>

<script>
import { keyBy, unionBy, differenceBy } from 'lodash';
import http from '@/http';

export default {
  name: 'GdlSelect',
  model: { prop: 'value', event: 'change' },
  props: {
    value: { type: [String, Number, Array], default: null },
    options: { type: [Array, Function], default: () => [] },
    placeholder: { type: String, default: '' },
    readonly: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    multiple: { type: Boolean, default: false },
    filterable: { type: Boolean, default: false },
    clearable: { type: Boolean, default: true },
    remote: { type: Object, default: null },

    size: { type: String, default: undefined },
    transfer: { type: Boolean, default: undefined },

    initOptions: { type: Array, default: () => [] },
  },
  data() {
    return {
      remoteData: {
        cacheLoading: false,
        cached: [],
        options: [],
        loading: false,
        request: null,
      },
    };
  },
  computed: {
    isDisabled() {
      return this.readonly || this.disabled || this.remoteData.cacheLoading;
    },
    isFilterable() {
      return this.filterable && !this.readonly;
    },
    isClearable() {
      return this.clearable && !this.readonly;
    },
    computedOptions() {
      const { options } = this;
      return (typeof options === 'function') ? options.call(this) : options;
    },
    optionsList() {
      return this.remote ?
        this.remoteData.options :
        this.computedOptions;
    },
    optionsCached() {
      if (!this.remote) return [];
      const { initOptions = [] } = this.remote;
      const { cached, options } = this.remoteData;
      const optionsCached = unionBy(cached, initOptions, 'value');
      return differenceBy(optionsCached, options, 'value');
    },
    optionsByValue() {
      return [
        this.remoteData.options,
        this.remoteData.cached,
        this.computedOptions,
      ] |> (_ => unionBy(..._, 'value'))
        |> (_ => keyBy(_, 'value'));
    },
  },
  watch: {
    value: {
      handler(value) {
        if (!this.remote) return;
        const { remoteData, optionsByValue } = this;

        if (!value || !value.length) {
          remoteData.cached = [];
          return;
        }

        const notFound = [];
        const cached = (Array.isArray(value) ? value : [value]).map((id) => {
          if (optionsByValue[id]) return optionsByValue[id];
          notFound.push(id);
          return null;
        }).filter(v => !!v);
        if (notFound.length) {
          this.cacheMethod(notFound);
        }
        remoteData.cached = cached;
      },
      immediate: true,
    },
  },
  methods: {
    onChange(value) {
      if (Array.isArray(value)) {
        this.$emit('change', value.length ? value : null);
      } else {
        this.$emit('change', value || null);
      }
    },
    stopLoading() {
      // Avoid showing No result message
      setTimeout(() => {
        const { remoteData, $nextTick } = this;
        $nextTick(() => { remoteData.loading = false; });
      }, 0);
    },
    async fetchOptions(query, reason) {
      const { remote, remoteData } = this;
      try {
        const { url, deserialize, params } = remote;

        remoteData.loading = true;
        remoteData.request = http.CancelToken.source();
        const response = await http.get(url, {
          cancelToken: remoteData.request.token,
          'axios-debounce': { id: `SelectFilter_${this._uid}_${reason}_${this.url}` }, // eslint-disable-line no-underscore-dangle
          params: params(query),
        });
        remoteData.request = null;
        this.stopLoading();

        const { data: options } = response.data;
        return options.map(deserialize);
      } catch (error) {
        if (http.isCancel(error)) {
          // canceled by next request
          return null;
        }
        this.stopLoading();
        throw error;
      }
    },
    async cacheMethod(values) {
      const { remoteData } = this;
      remoteData.cacheLoading = true;
      try {
        const options = await this.fetchOptions({ values }, 'cache');
        if (!options) return;
        remoteData.cached = unionBy(options, remoteData.cached, 'value');
      } finally {
        remoteData.cacheLoading = false;
      }
    },
    async remoteMethod(query) {
      const { remoteData } = this;
      if (query !== '') {
        const options = await this.fetchOptions({ query }, 'query');
        if (!options) return;
        remoteData.options = options;
      } else {
        if (remoteData.request) {
          remoteData.request.cancel('Empty query');
          remoteData.request = null;
        }
        this.stopLoading();
        remoteData.options = [];
      }
    },
  },
};
</script>

<style lang="scss" module>
.select[readonly] {
  input[disabled] {
    cursor: auto;
    color: #495060;
    padding: unset;
    &::placeholder {
      color: transparent;
    }
  }

  :global(.ivu-select-selection) {
    cursor: auto;
    background-color: unset;
    color: #495060;
  }
  :global(.ivu-select-placeholder) {
    display: none;
  }

  :global(.ivu-tag) {
    cursor: auto;
  }
  :global(.ivu-tag .ivu-icon) {
    display: none;
  }
}
</style>
